// ─────────────────────────────────────────
//  Fetstil och kursiv i frågetext och återkoppling
// ─────────────────────────────────────────
// Lagrad text: **ord** är fetstil, _ord_ kursiv och **_ord_** båda; "\n"
// skiljer stycken. Bara markeringar vid ordgräns räknas, så att 2**3**2, x**2
// och snake_case lämnas orörda och ensamma tecken visas som de står. Lärarens
// editor läser och skriver samma format, så studentvyn tolkar aldrig HTML.

const BOLD_RX = /(^|[^\p{L}\p{N}*])\*\*([^\s*](?:.*?[^\s*])??)\*\*(?![\p{L}\p{N}*])/gu;
const ITALIC_RX = /(^|[^\p{L}\p{N}_])_([^\s_](?:.*?[^\s_])??)_(?![\p{L}\p{N}_])/gu;
const WORD_RX = /[\p{L}\p{N}]/u;
const SPACE_RX = /\s/u;

function splitMarked(s, pattern) {
  const rx = new RegExp(pattern);
  const parts = [];
  let last = 0;
  let m;
  while ((m = rx.exec(s))) {
    const start = m.index + m[1].length;
    if (start > last) parts.push({ text: s.slice(last, start), on: false });
    parts.push({ text: m[2], on: true });
    last = rx.lastIndex;
  }
  if (last < s.length || parts.length === 0) parts.push({ text: s.slice(last), on: false });
  return parts;
}

// Delar texten i bitar [{ text, bold, italic }]. Text utan formatering blir
// en enda bit. Fetstilen tolkas först, kursiv inuti den: **_ord_**.
function splitFormatting(text) {
  const out = [];
  for (const b of splitMarked(String(text ?? ""), BOLD_RX)) {
    for (const i of splitMarked(b.text, ITALIC_RX)) out.push({ text: i.text, bold: b.on, italic: i.on });
  }
  return out;
}

// Texten utan formateringstecken, för sökning
function stripFormatting(text) {
  return splitFormatting(text).map(p => p.text).join("");
}

// ── Från editorn till lagrad text ──────────────────────────────────────────

const isWord = c => WORD_RX.test(c.ch);
const isSpace = c => SPACE_RX.test(c.ch);

// Blanktecken i kanten av en markering hamnar utanför den
function trimEdges(chars, key) {
  for (let i = 0; i < chars.length; ) {
    if (!chars[i][key]) { i++; continue; }
    let j = i;
    while (j < chars.length && chars[j][key]) j++;
    for (let k = i; k < j && isSpace(chars[k]); k++) chars[k][key] = false;
    for (let k = j - 1; k >= i && isSpace(chars[k]); k--) chars[k][key] = false;
    i = j;
  }
}

// Studentvyn känner bara igen markeringar vid ordgräns. Därför gäller
// formatering hela ord: är en del av ett ord fet blir hela ordet det, och en
// markering som börjar eller slutar tätt intill ett ord tar med ordet.
function snapToWords(chars, key) {
  let changed = true;
  while (changed) {
    changed = false;
    // Först blanktecken i kanten, så att " x" inte drar med ordet före
    trimEdges(chars, key);
    for (let i = 0; i < chars.length; ) {
      if (!isWord(chars[i])) { i++; continue; }
      let j = i;
      while (j < chars.length && isWord(chars[j])) j++;
      const word = chars.slice(i, j);
      if (word.some(c => c[key]) && word.some(c => !c[key])) {
        word.forEach(c => { c[key] = true; });
        changed = true;
      }
      i = j;
    }
    for (let i = 0; i < chars.length; i++) {
      if (!chars[i][key]) continue;
      for (const n of [i - 1, i + 1]) {
        if (n >= 0 && n < chars.length && !chars[n][key] && isWord(chars[n])) {
          chars[n][key] = true;
          changed = true;
        }
      }
    }
  }
}

function emitLine(chars) {
  let out = "";
  for (let i = 0; i < chars.length; ) {
    let j = i;
    while (j < chars.length && chars[j].bold === chars[i].bold) j++;
    // Kursiv inom varje fet eller ofet del, så att den alltid hamnar innanför: **_ord_**
    const seg = chars.slice(i, j);
    trimEdges(seg, "italic");
    let s = "";
    let on = false;
    for (const c of seg) {
      if (c.italic !== on) { s += "_"; on = c.italic; }
      s += c.ch;
    }
    if (on) s += "_";
    out += chars[i].bold ? `**${s}**` : s;
    i = j;
  }
  return out;
}

function sameAsParsed(line, chars) {
  const parsed = [];
  for (const p of splitFormatting(line)) for (const ch of p.text) parsed.push({ ch, bold: p.bold, italic: p.italic });
  return parsed.length === chars.length && parsed.every((p, k) => p.ch === chars[k].ch && p.bold === chars[k].bold && p.italic === chars[k].italic);
}

// Ett stycke [{ text, bold, italic }] → lagrad rad. Skulle raden inte tolkas
// tillbaka till samma formatering (t.ex. text som själv innehåller * eller _
// i kanten) sparas den hellre utan kursiv, och i sista hand utan formatering.
function formatLine(runs) {
  const chars = [];
  for (const r of runs) for (const ch of r.text) chars.push({ ch, bold: !!r.bold, italic: !!r.italic });
  snapToWords(chars, "bold");
  snapToWords(chars, "italic");
  const full = emitLine(chars);
  if (sameAsParsed(full, chars)) return full;
  const boldOnly = chars.map(c => ({ ...c, italic: false }));
  const noItalic = emitLine(boldOnly);
  if (sameAsParsed(noItalic, boldOnly)) return noItalic;
  return chars.map(c => c.ch).join("");
}

// Lagrad text → editorns dokument (ProseMirror-JSON). Varje rad blir ett stycke.
function toEditorDoc(text) {
  const lines = String(text ?? "").split("\n");
  return {
    type: "doc",
    content: lines.map(line => {
      const content = splitFormatting(line).filter(p => p.text).map(p => {
        const marks = [];
        if (p.bold) marks.push({ type: "bold" });
        if (p.italic) marks.push({ type: "italic" });
        return marks.length ? { type: "text", text: p.text, marks } : { type: "text", text: p.text };
      });
      return content.length ? { type: "paragraph", content } : { type: "paragraph" };
    }),
  };
}

// Editorns dokument → lagrad text. En radbrytning (Skift+Enter, eller <br> från
// Word) blir ett nytt stycke, eftersom "\n" är det enda den lagrade texten har.
function fromEditorDoc(doc) {
  const lines = [];
  for (const block of doc?.content || []) {
    let runs = [];
    for (const node of block.content || []) {
      if (node.type === "hardBreak") { lines.push(runs); runs = []; continue; }
      if (node.type !== "text") continue;
      const marks = (node.marks || []).map(m => m.type);
      runs.push({ text: node.text || "", bold: marks.includes("bold"), italic: marks.includes("italic") });
    }
    lines.push(runs);
  }
  return lines.map(formatLine).join("\n");
}

export { splitFormatting, stripFormatting, toEditorDoc, fromEditorDoc };
